<script setup lang="ts">
import { computed } from 'vue';
import type { ProviderUsage, WindowUsage } from '../api/types';
import { clamp, colorFromId, formatNumber, formatPercent, formatResetAt } from '../utils/format';

interface Props {
  readonly provider: ProviderUsage;
}

const props = defineProps<Props>();

const hasWindows = computed(() => props.provider.windows.length > 0);
const avatarColor = computed(() => colorFromId(props.provider.id));
const initials = computed(() => {
  const name = props.provider.name.trim();
  if (name.length === 0) return '?';
  // Chinese / multi-byte: take first char to keep it predictable.
  return Array.from(name)[0] ?? '?';
});

const statusBadge = computed(() => {
  if (!props.provider.enabled) {
    return { label: '未配置', cls: 'badge--off' };
  }
  if (!props.provider.ok) {
    return { label: '查询失败', cls: 'badge--err' };
  }
  return { label: '正常', cls: 'badge--ok' };
});

const cardClass = computed(() => ({
  'provider-card': true,
  'provider-card--error': props.provider.enabled && !props.provider.ok,
  'provider-card--off': !props.provider.enabled,
}));

function ratioClass(ratio: number): string {
  const r = clamp(ratio, 0, 1);
  if (r >= 0.85) return 'window__bar-fill--danger';
  if (r >= 0.6) return 'window__bar-fill--warn';
  return 'window__bar-fill--ok';
}

function windowRemaining(window: WindowUsage): number {
  if (typeof window.remaining === 'number' && Number.isFinite(window.remaining)) {
    return window.remaining;
  }
  return Math.max(window.total - window.used, 0);
}

function windowPercent(window: WindowUsage): string {
  return formatPercent(window.ratio);
}

</script>

<template>
  <article
    :class="cardClass"
    :aria-labelledby="`provider-${provider.id}-name`"
  >
    <header class="provider-card__head">
      <div
        class="provider-card__avatar"
        :style="{ background: avatarColor }"
        aria-hidden="true"
      >
        {{ initials }}
      </div>
      <div class="provider-card__title">
        <div
          :id="`provider-${provider.id}-name`"
          class="provider-card__name"
        >{{ provider.name }}</div>
        <div class="provider-card__id">{{ provider.id }}</div>
      </div>
      <div class="provider-card__badges">
        <span :class="['badge', statusBadge.cls]">{{ statusBadge.label }}</span>
        <span
          v-if="provider.maskedKey"
          class="masked-key"
          :title="`识别码：${provider.maskedKey}`"
        >{{ provider.maskedKey }}</span>
      </div>
    </header>

    <div
      v-if="!hasWindows && !provider.error"
      class="provider-card__status-hint"
    >
      <template v-if="!provider.enabled">未配置 — 缺少对应环境变量。</template>
      <template v-else>暂无用量窗口数据。</template>
    </div>

    <div
      v-else-if="provider.error"
      class="provider-card__status"
      role="status"
    >
      <div class="provider-card__status-error">
        <span aria-hidden="true">⚠</span>
        <span>{{ provider.error }}</span>
      </div>
      <div
        v-if="provider.hint"
        class="provider-card__status-hint"
      >{{ provider.hint }}</div>
    </div>

    <div
      :id="`provider-${provider.id}-windows`"
      class="provider-card__windows"
    >
      <div
        v-for="(win, idx) in provider.windows"
        :key="`${provider.id}-win-${idx}`"
        class="window"
      >
        <div class="window__head">
          <span class="window__label">{{ win.label }}</span>
          <span>{{ windowPercent(win) }}</span>
        </div>
        <div
          class="window__bar"
          role="progressbar"
          :aria-valuemin="0"
          :aria-valuemax="100"
          :aria-valuenow="Math.round(clamp(win.ratio, 0, 1) * 100)"
          :aria-label="`${win.label} 已用 ${windowPercent(win)}`"
        >
          <div
            class="window__bar-fill"
            :class="ratioClass(win.ratio)"
            :style="{ width: `${clamp(win.ratio, 0, 1) * 100}%` }"
          ></div>
        </div>
        <div class="window__foot">
          <span class="window__remaining">
            剩 {{ formatNumber(windowRemaining(win)) }} / {{ formatNumber(win.total) }}
          </span>
          <span class="window__reset">
            <svg
              class="window__reset-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
              <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
            </svg>
            {{ formatResetAt(win.resetAt) }}
          </span>
        </div>
      </div>
    </div>
  </article>
</template>

